import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vocabularyService } from '../../api/vocabularyService';
import { VocabularyItem } from '../vocabulary/VocabularyItem';

interface WordPopupState {
    word: string;
    x: number;
    y: number;
}

interface WordClickPopupProps {
    popup: WordPopupState | null;
    onClose: () => void;
    onAdd?: (word: string) => void;
    // When set, the popup checks whether the clicked word already exists in the user's
    // vocabulary (in this language) and shows "Show" (→ Edit Word) instead of "+ Add" for a
    // match. Omitted by every other call site, which keeps their current "+ Add"-only behavior.
    learningLanguage?: string;
}

const WordClickPopup: React.FC<WordClickPopupProps> = ({ popup, onClose, onAdd, learningLanguage }) => {
    const navigate = useNavigate();
    const ref = useRef<HTMLDivElement>(null);
    const [existingItem, setExistingItem] = useState<VocabularyItem | null>(null);
    const [checking, setChecking] = useState(false);

    // word (lower-cased) → item, fetched once per language per mount with a single unpaginated
    // call (getVocabularyByLanguage) instead of up to 10 paginated substring searches per click.
    // Scoped to this component instance on purpose: adding a word navigates away (unmounting the
    // lesson/page that owns this popup), so the next mount always starts from fresh data.
    const lookupCache = useRef(new Map<string, Promise<Map<string, VocabularyItem>>>());

    useEffect(() => {
        if (!popup || !learningLanguage) {
            setExistingItem(null);
            setChecking(false);
            return;
        }
        let cancelled = false;
        setChecking(true);
        setExistingItem(null);

        let lookup = lookupCache.current.get(learningLanguage);
        if (!lookup) {
            lookup = vocabularyService.getVocabularyByLanguage(learningLanguage).then(
                (items) => new Map(items.map((item) => [item.word.toLowerCase(), item] as [string, VocabularyItem])),
            );
            lookup.catch(() => lookupCache.current.delete(learningLanguage)); // retry on the next click
            lookupCache.current.set(learningLanguage, lookup);
        }

        lookup
            .then((byWord) => { if (!cancelled) setExistingItem(byWord.get(popup.word.toLowerCase()) ?? null); })
            .catch(() => { if (!cancelled) setExistingItem(null); })
            .finally(() => { if (!cancelled) setChecking(false); });
        return () => { cancelled = true; };
    }, [popup, learningLanguage]);

    useEffect(() => {
        if (!popup) return;
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('keydown', handleKey);
        document.addEventListener('mousedown', handleClick);
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.removeEventListener('mousedown', handleClick);
        };
    }, [popup, onClose]);

    if (!popup) return null;

    const handleAdd = () => {
        onClose();
        if (onAdd) {
            onAdd(popup.word);
        } else {
            navigate('/addWord', { state: { word: popup.word } });
        }
    };

    const handleShow = () => {
        onClose();
        navigate('/EditWord', { state: { selectedItem: existingItem?.id } });
    };

    const translations = existingItem?.translation?.filter(Boolean) ?? [];

    return (
        <div
            ref={ref}
            style={{
                position: 'fixed',
                top: popup.y + 12,
                left: popup.x,
                zIndex: 2000,
                background: 'var(--nav-bg, #1a1f2e)',
                border: '0.5px solid rgba(255,255,255,0.12)',
                borderRadius: 10,
                boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
                padding: '10px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                minWidth: 160,
                maxWidth: 260,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                    color: '#5DCAA5',
                    fontWeight: 600,
                    fontSize: 14,
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {popup.word}
                </span>
                <button
                    onClick={existingItem ? handleShow : handleAdd}
                    disabled={checking}
                    style={{
                        marginLeft: 'auto',
                        background: '#5DCAA5',
                        color: '#04342C',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: checking ? 'default' : 'pointer',
                        opacity: checking ? 0.6 : 1,
                        whiteSpace: 'nowrap',
                    }}
                >
                    {existingItem ? 'Show' : '+ Add'}
                </button>
            </div>
            {existingItem && translations.length > 0 && (
                <span style={{
                    color: 'rgba(255,255,255,0.75)',
                    fontSize: 12,
                    lineHeight: 1.4,
                }}>
                    {translations.join(', ')}
                </span>
            )}
        </div>
    );
};

function extractWord(node: Node | null, offset: number): string {
    if (!node || node.nodeType !== Node.TEXT_NODE) return '';
    const text = node.textContent || '';
    let start = offset;
    let end = offset;
    while (start > 0 && /[\w'-]/.test(text[start - 1])) start--;
    while (end < text.length && /[\w'-]/.test(text[end])) end++;
    const word = text.slice(start, end).replace(/^['-]+|['-]+$/g, '');
    // \w also matches "_" and digits, so a gap placeholder like "______" (lesson blanks) or a
    // bare number would otherwise come back as a "word" and offer to add it.
    return /\p{L}/u.test(word) ? word : '';
}

export function getWordAtPoint(x: number, y: number): string {
    // document.caretRangeFromPoint is WebKit/Blink-only (Chrome, Safari, Edge) — Firefox
    // implements the equivalent as document.caretPositionFromPoint instead, so without this
    // fallback the word-click-to-add feature silently does nothing there.
    if (document.caretRangeFromPoint) {
        const range = document.caretRangeFromPoint(x, y);
        if (!range) return '';
        return extractWord(range.startContainer, range.startOffset);
    }
    const caretDoc = document as Document & {
        caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    };
    if (caretDoc.caretPositionFromPoint) {
        const pos = caretDoc.caretPositionFromPoint(x, y);
        if (!pos) return '';
        return extractWord(pos.offsetNode, pos.offset);
    }
    return '';
}

export default WordClickPopup;
